/**
 * articleRepo.js — merged repository for CloudLab articles.
 *
 * Phase 4A abstraction (mirrors caseStudyRepo.js). Public components call
 * these hooks instead of reaching directly into articles.js or the DB.
 *
 *   1. CODE-MANAGED — static records in src/data/articles.js
 *                      (strategic, evidence-governed CloudLab longreads).
 *   2. DB-MANAGED   — rows in the backend `blogs` collection
 *                      (AI-generated via Admin). Served via /api/blogs.
 *
 * Code-managed wins on slug collision (defensive — a future AI-generated
 * blog cannot accidentally hijack a strategic article URL).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ARTICLES,
  getPublishedArticles,
  getArticleBySlug as getCodeManagedBySlug,
} from "@/data/articles";

const CODE_SLUGS = new Set(ARTICLES.map((a) => a.slug));

/* ------------------------------------------------------------------ */
/*  SHAPE ADAPTER                                                      */
/* ------------------------------------------------------------------ */
/* Normalise a DB blog record into a shape BlogPost.jsx can consume    */
/* alongside code-managed articles. Both end up on the same renderer.  */
function normalizeDbBlog(rec) {
  if (!rec) return null;
  return {
    source: "db",
    status: rec.published ? "published" : "draft",
    slug: rec.slug,
    title: rec.title,
    excerpt: rec.excerpt || "",
    category: rec.category || "Insights",
    primaryService: null,
    relatedServices: [],
    relatedIndustries: [],
    relatedCaseStudies: [],
    relatedArticles: [],
    author: { name: "CloudLab Technologies", role: null, url: "https://www.cloudlabweb.com/" },
    publishedAt: rec.created_at,
    updatedAt: rec.updated_at || rec.created_at,
    heroImage: rec.cover_image || null,
    seo: {
      title: rec.title,
      description: rec.excerpt || rec.title,
      ogImage: rec.cover_image || null,
      noindex: false,
    },
    // Legacy markdown body — kept on `legacyMarkdown` so BlogPost can
    // render DB blogs via the existing md pipeline.
    legacyMarkdown: rec.content_md || "",
    tags: rec.tags || [],
    faqs: rec.faqs || [],
    answerProfile: {},
    sections: [],
    primaryCTA: null,
    secondaryCTA: null,
  };
}

function normalizeCodeArticle(rec) {
  if (!rec) return null;
  return { ...rec, source: "code" };
}

/* ------------------------------------------------------------------ */
/*  HOOKS                                                              */
/* ------------------------------------------------------------------ */
export function usePublishedArticles() {
  const codeManaged = getPublishedArticles().map(normalizeCodeArticle);
  const [db, setDb] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/blogs")
      .then((r) => {
        if (!mounted) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setDb(list
          .filter((rec) => !CODE_SLUGS.has(rec.slug))
          .map(normalizeDbBlog)
          .filter(Boolean));
      })
      .catch(() => { if (mounted) setDb([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Sort by publishedAt desc.
  const items = [...codeManaged, ...db].sort((a, b) => {
    const ta = new Date(a.publishedAt || 0).getTime();
    const tb = new Date(b.publishedAt || 0).getTime();
    return tb - ta;
  });
  return { items, loading };
}

export function useArticle(slug) {
  const code = getCodeManagedBySlug(slug);
  const codeMatch = code && code.status === "published";
  const [db, setDb] = useState(null);
  const [loading, setLoading] = useState(!codeMatch);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (codeMatch) return;
    let mounted = true;
    api.get(`/blogs/${slug}`)
      .then((r) => { if (mounted) setDb(normalizeDbBlog(r.data)); })
      .catch((e) => { if (mounted) setError(e); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug, codeMatch]);

  if (codeMatch) return { data: normalizeCodeArticle(code), loading: false, error: null };
  return { data: db, loading, error };
}
